import { Component, inject, output } from "@angular/core";
import { Container } from "@components/container/container";
import { AnalyticsService } from "@services/analytics.service";
import { AuthState } from "@states/auth.state";

@Component({
  selector: "app-welcome-screen",
  imports: [Container],
  template: `
    <app-container>
      <div class="welcome">
        <div class="welcome__content">
          <img class="welcome__alloprof-logo" src="assets/logo/alloprof.svg" alt="Logo AlloProf" />
          <div class="welcome__text">
            <h1 class="welcome__text-top" data-testid="welcome-screen-title">
              Exercice interactif
              <span class="welcome__text-title">Homophones</span>
            </h1>
            <p class="welcome__text-bottom">Entraine-toi et deviens pro de l'utilisation des homophones.</p>
          </div>

          @if (!authState.currentUser()) {
            <div class="welcome__content-login-informative">
              <img src="assets/flo/flo-magnifying-glass.svg" alt="Flo Loupe" />
              <div class="welcome__text-login-informative">
                <p>Tu n'es pas connecté !</p>
                <p>Connecte-toi à Alloprof pour conserver tes progrès.</p>
              </div>
            </div>
          }

          <button class="welcome__start-btn btn btn-primary" (click)="startExerciser()">Commencer</button>
        </div>
        <div class="welcome__footer">
          <div class="welcome__content-ia-informative" data-testid="ai-notice">
            <img src="assets/icons/watermarek-ia.svg" alt="Découvre comment Alloprof utilise l'intelligence artificielle" />
            <div class="welcome__text-ia-informative">
              <p>Nos profs sont au cœur de la production de nos contenus.</p>
              <p>L'IA a été un soutien pour l'accélérer.</p>
              <p>
                En savoir plus sur notre
                <a target="_blank" href="https://www.alloprof.qc.ca/fr/pages/l-intelligence-artificielle-et-alloprof"> utilisation de l'IA.</a>
              </p>
            </div>
          </div>
        </div>
        <img class="welcome__flo" [class.welcome__flo--logged-in]="authState.currentUser()" src="assets/flo/flo-wingsuit.svg" alt="Flo" />
      </div>
    </app-container>
  `,
  styles: `
    @use "responsive" as *;
    @use "typography" as *;

    .welcome {
      display: flex;
      flex-direction: column;
      flex: 1;
      justify-content: space-between;
      position: relative;
    }

    .welcome__content {
      display: flex;
      justify-content: center;
      flex-direction: column;
      align-items: center;
      max-width: 840px;
      margin: 16px auto 0 auto;
      gap: 16px;
      position: relative;
      z-index: 1000;

      @include screenWidth("from-tablet") {
        margin-top: 28px;
        gap: 20px;
      }
    }

    .welcome__alloprof-logo {
      display: block;
      width: 150px;
      margin-left: auto;
      margin-right: auto;

      @include mobileScreenHeight("small") {
        margin-bottom: 30px;
      }

      @include mobileScreenHeight("medium") {
        margin-bottom: 110px;
      }
      @include mobileScreenHeight("tall") {
        margin-bottom: 160px;
      }
      @include mobileScreenHeight("huge") {
        margin-bottom: 210px;
      }
      @include screenWidth("tablet-only") {
        margin-bottom: 90px;
      }

      @include screenWidth("from-desktop") {
        margin-bottom: 44px;
        width: 185px;
      }
    }

    .welcome__text {
      text-align: center;
    }

    .welcome__text-top {
      @include subtitle3();

      @include screenWidth("from-tablet") {
        @include subtitle1();
      }
    }

    .welcome__text-title {
      display: block;
      margin: 12px auto;

      @include titleH2();

      @include screenWidth("to-mobile-small") {
        @include titleH2();
      }

      @include screenWidth("from-desktop") {
        @include titleH1();
      }
    }

    .welcome__text-bottom {
      @include body2();

      @include screenWidth("from-tablet") {
        @include body1();
      }
    }

    .welcome__content-login-informative {
      display: flex;
      align-items: center;
      justify-content: center;
      width: calc(100% - 20px);
      max-width: 522px;
      border-radius: 20px;
      background-color: #ffffff;
      border: 2px solid #ffa670;
      box-shadow: 0 4px 0 #ffa670;
      padding: 4px 8px;
      gap: 12px;

      @include screenWidth("from-mobile-medium") {
        padding: 0 20px;
        gap: 20px;
      }

      img {
        width: 80px;
        height: 100px;

        @include screenWidth("from-tablet") {
          width: 120px;
          height: 120px;
        }
      }

      .welcome__text-login-informative {
        display: flex;
        flex-direction: column;
        gap: 2px;

        p:first-of-type {
          @include subtitle3();
        }

        p:not(:first-of-type) {
          color: rgba(21, 41, 66, 0.65);
          @include body3();
        }
      }
    }

    .welcome__footer {
      display: flex;
      justify-content: center;
      z-index: 600;
    }

    .welcome__content-ia-informative {
      display: flex;
      align-items: center;
      justify-content: center;
      max-width: 522px;
      padding: 8px;
      gap: 12px;
      border-radius: 20px;
      background: rgba(0, 0, 0, 0.7);
      width: calc(100% - 20px);

      @include screenWidth("from-mobile-medium") {
        gap: 20px;
        padding: 20px;
      }

      @include screenWidth("mobile-only") {
        img {
          display: none;
        }
      }

      .welcome__text-ia-informative {
        @include body3();

        a,
        p {
          color: #ffffff;
        }
      }
    }

    .welcome__flo {
      transform: scale(1) translateY(2%) translate(-103%);
      transform-origin: bottom;
      position: absolute;
      left: 50%;
      right: 50%;
      bottom: 0;
      z-index: 500;
      width: 364px;
      height: 369px;

      // the user is logged in
      &.welcome__flo--logged-in {
        transform: scale(1) translateY(-19%) translate(-103%);

        @include screenWidth("from-tablet") {
          transform: scale(1.2) translateY(-10%) translate(-108%);
        }

        @include screenWidth("from-desktop") {
          transform: scale(1.2) translateY(-7%) translate(-122%);
        }
      }

      //user not logged in
      @include mobileScreenHeight("small") {
        transform: scale(1) translateY(2%) translate(-103%);
      }

      @include screenWidth("from-tablet") {
        transform: scale(1.2) translateY(-10%) translate(-108%);
      }

      @include screenWidth("from-desktop") {
        transform: scale(1.2) translateY(-7%) translate(-122%);
      }
    }
  `,
})
export class WelcomeScreen {
  private analyticsService = inject(AnalyticsService);
  authState = inject(AuthState);

  exerciserHasStarted = output<void>();

  startExerciser() {
    this.analyticsService.trackExerciserStart();
    this.exerciserHasStarted.emit();
  }
}
